import type { WallLateralDatumAnchorAll } from "building-types/datums/datumsTypes";
import {
  PropInstanceHorizontalAlignment,
  PropInstanceLateralAlignment,
  PropInstanceVerticalAlignment,
} from "building-types/datums/datumsTypesEnum";
import type { PropInstanceHandle, RoomLayoutInstanceHandle } from "building-types/handles/docEntityHandleTypes";
import { getBuiltinProp } from "building-types/props/getBuiltinPropLibrary";
import type { PropAsset } from "building-types/props/PropAsset";
import type {
  PropMaterialOverrides,
  Prop,
  PropFloorPlanTagProps,
  PropAttributeTable,
} from "building-types/props/propTypes";
import { PlacementStyleVersion } from "building-types/props/propTypesEnum";
import type { EntityInstanceType } from "building-types/roomLayouts/roomLayoutEligibleDocEntity";
import type { Room } from "building-types/rooms/roomTypes";
import type { InteriorFloorMaterialOverride } from "building-types/settings/floorMaterialSettings";
import type { PropCatalogType } from "building-types/settings/settingsTypesEnum";
import type { ViewEntityDescriptor } from "building-types/views/viewEntityTypes";
import { GLTF_ASSET_SCALE } from "common/constants/assetConstants";
import { WALL_OFFSET_INTERIOR } from "common/constants/buildingObjects";
import type { Table } from "common/data/table";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { radianToDegree } from "common/utils/math";
import { aabb3Copy, aabb3Scale } from "geom/aabb3";
import type { AABB3 } from "geom/aabb3Types";
import { aabb3New } from "geom/aabb3Types";
import { mat3Mult, mat3RotateAroundPoint, mat3Translate } from "geom/mat3";
import { polyLine2AABB2, polyLine2EdgeEvalOffset, polyLine2EdgeNormal, polyLine2Transform } from "geom/polyLine2";
import type { LineAnchor, PolyLine2 } from "geom/polyLine2Types";
import { rect2ByTwoPoints } from "geom/rect2";
import { vec2AngleBetween, vec2Rotate } from "geom/vec2";
import { propNew } from "../propTools";

function propAABB3FromSettings(propAsset: PropAsset): AABB3 | null {
  if (!propAsset) {
    return null;
  }
  return aabb3Scale(aabb3Copy(propAsset.boundingBox), GLTF_ASSET_SCALE);
}

interface PropOffsetOptionsBase extends PropFloorPlanTagProps {
  propId: string;
  room: Room;
  edge: PolyLine2;
  param?: number;
  paramAnchor?: LineAnchor;
  paramNormalized?: boolean;
  offset?: number;
  rotate?: number; // in radians
  z?: number;
  centerX?: boolean;
  centerY?: boolean;
  horizontalAlignment?: PropInstanceHorizontalAlignment;
  verticalAlignment?: PropInstanceVerticalAlignment;
  lateralDatum?: WallLateralDatumAnchorAll;
  lateralAlignment?: PropInstanceLateralAlignment;
  handle?: PropInstanceHandle | RoomLayoutInstanceHandle;
  handleAuxiliary?: PropInstanceHandle;
  useWallStripMaxOffset?: boolean;
  mirror?: boolean;
  mirrorPropX?: boolean;
  mirrorPropY?: boolean;
  uniformScale?: boolean;
  scaleX?: number;
  scaleY?: number;
  scaleZ?: number;
  labels?: Table<boolean>;
  placementStyle?: PlacementStyleVersion;
  floorMaterial?: InteriorFloorMaterialOverride;
  descriptor?: ViewEntityDescriptor;
  catalogType: PropCatalogType;
  materialOverrides?: PropMaterialOverrides;
  useExteriorPalette?: boolean;
  isDeprecatedWallProp?: boolean;
  entityInstanceType?: EntityInstanceType;
}

interface PropOffsetOptions extends PropOffsetOptionsBase {
  settings: PropAttributeTable;
}

interface PropAssetOffsetOptions extends PropOffsetOptionsBase {
  propAsset: PropAsset;
  isExterior?: boolean;
}

export async function propOffsetFromLine2(options: PropOffsetOptions) {
  const assetId = options.settings[options.propId]?.propAssetId ?? options.propId;
  const propAsset = await getBuiltinProp(assetId);
  assertIsNotNullOrUndefined(propAsset);
  return propAssetOffsetFromLine2({
    ...options,
    propAsset,
  });
}

export function propAssetOffsetFromLine2({
  propAsset,
  propId,
  room,
  edge,
  param = 0.5,
  paramNormalized = true,
  horizontalAlignment,
  lateralAlignment,
  verticalAlignment,
  offset: offsetDistance = WALL_OFFSET_INTERIOR,
  rotate: propRotate = 0, // in radians
  z = 0,
  placementStyle = PlacementStyleVersion.VERSION_0,
  handle,
  handleAuxiliary,
  mirror = false,
  mirrorPropX = false,
  mirrorPropY = false,
  uniformScale = true,
  scaleX = 1,
  scaleY = 1,
  scaleZ = 1,
  labels = {},
  floorMaterial,
  descriptor,
  catalogType,
  materialOverrides,
  useExteriorPalette,
  isDeprecatedWallProp = false,
  entityInstanceType,
  floorPlanTag,
  floorPlanTagOffsetX,
  floorPlanTagOffsetY,
  isExterior,
}: PropAssetOffsetOptions) {
  const [wallNormX, wallNormY] = polyLine2EdgeNormal(edge, 0);
  const rotate = vec2AngleBetween(0, 1, -wallNormX, -wallNormY) + Math.PI;
  const [offsetX, offsetY] = polyLine2EdgeEvalOffset(edge, 0, -offsetDistance, param, paramNormalized);

  return propAtPoint({
    propAsset,
    propId,
    room,
    horizontalAlignment,
    lateralAlignment,
    verticalAlignment,
    x: offsetX,
    y: offsetY,
    z,
    radians: propRotate + rotate,
    placementStyle,
    catalogType,
    handle,
    handleAuxiliary,
    mirror,
    mirrorPropX,
    mirrorPropY,
    uniformScale,
    scaleX,
    scaleY,
    scaleZ,
    labels,
    floorMaterial,
    descriptor,
    materialOverrides,
    useExteriorPalette,
    isDeprecatedWallProp,
    entityInstanceType,
    floorPlanTag,
    floorPlanTagOffsetX,
    floorPlanTagOffsetY,
    isExterior,
  });
}

function propAtPoint({
  propAsset,
  propId,
  room,
  x,
  y,
  z,
  horizontalAlignment = PropInstanceHorizontalAlignment.ORIGIN,
  verticalAlignment = PropInstanceVerticalAlignment.ORIGIN,
  lateralAlignment = PropInstanceLateralAlignment.ORIGIN,
  radians = 0,
  placementStyle = PlacementStyleVersion.VERSION_0,
  handle,
  handleAuxiliary,
  mirror = false,
  mirrorPropX = false,
  mirrorPropY = false,
  uniformScale = true,
  scaleX = 1,
  scaleY = 1,
  scaleZ = 1,
  labels = {},
  floorMaterial,
  descriptor,
  catalogType,
  materialOverrides,
  useExteriorPalette,
  isDeprecatedWallProp = false,
  entityInstanceType,
  floorPlanTag,
  floorPlanTagOffsetX,
  floorPlanTagOffsetY,
  isExterior,
}: {
  propAsset: PropAsset;
  propId: string;
  room: Room;
  x: number;
  y: number;
  z: number;
  horizontalAlignment?: PropInstanceHorizontalAlignment;
  verticalAlignment?: PropInstanceVerticalAlignment;
  lateralAlignment?: PropInstanceLateralAlignment;
  radians?: number;
  placementStyle?: PlacementStyleVersion;
  handle?: PropInstanceHandle | RoomLayoutInstanceHandle;
  handleAuxiliary?: PropInstanceHandle;
  mirror?: boolean;
  mirrorPropX?: boolean;
  mirrorPropY?: boolean;
  uniformScale?: boolean;
  scaleX?: number;
  scaleY?: number;
  scaleZ?: number;
  labels?: Table<boolean>;
  floorMaterial?: InteriorFloorMaterialOverride;
  descriptor?: ViewEntityDescriptor;
  catalogType: PropCatalogType;
  materialOverrides?: PropMaterialOverrides;
  isDeprecatedWallProp?: boolean;
  useExteriorPalette?: boolean;
  entityInstanceType?: EntityInstanceType;
  floorPlanTag?: string;
  floorPlanTagOffsetX?: number;
  floorPlanTagOffsetY?: number;
  isExterior?: boolean;
} & PropFloorPlanTagProps): Prop {
  const bb = propAABB3FromSettings(propAsset) ?? aabb3New(0, 0, 0, 0, 0, 0);
  const originRelativeX = (bb.maxX + bb.minX) * 0.5;
  const originRelativeY = (bb.maxY + bb.minY) * 0.5;

  const [widthX, widthY] = vec2Rotate(originRelativeX, 0, radians);
  const [lengthX, lengthY] = vec2Rotate(0, originRelativeY, radians);
  const alignmentX = x,
    alignmentY = y;

  switch (placementStyle) {
    case PlacementStyleVersion.DEPRECATED:
      if (mirror && horizontalAlignment === PropInstanceHorizontalAlignment.CENTER) {
        const [widthX, widthY] = vec2Rotate(bb.maxX + bb.minX, 0, radians);
        x += widthX;
        y += widthY;
      }
      if (horizontalAlignment === PropInstanceHorizontalAlignment.CENTER) {
        x -= widthX;
        y -= widthY;
      }
      if (lateralAlignment === PropInstanceLateralAlignment.CENTERLINE) {
        x += lengthX;
        y += lengthY;
      }
      if (mirror) {
        mirrorPropX = !mirrorPropX;
      }
      break;

    case PlacementStyleVersion.VERSION_0: {
      const widthAdjust = propParamJustificationAdjustment(bb.minX, bb.maxX, horizontalAlignment);
      const lengthAdjust = propLateralJustificationAdjustment(bb.minY, bb.maxY, lateralAlignment);
      const heightAdjust = propVerticalJustificationAdjustment(bb.minZ, bb.maxZ, verticalAlignment);
      const [widthX, widthY] = vec2Rotate(widthAdjust, 0, radians);
      const [lengthX, lengthY] = vec2Rotate(0, lengthAdjust, radians);

      if (mirror) {
        mirrorPropX = !mirrorPropX;
      }
      const mirrorPropXAdjust = mirrorPropX ? 1 : -1;
      const mirrorPropYAdjust = mirrorPropY ? 1 : -1;
      x += widthX * mirrorPropXAdjust + lengthX * mirrorPropYAdjust;
      y += widthY * mirrorPropXAdjust + lengthY * mirrorPropYAdjust;
      z -= heightAdjust;
      break;
    }

    default:
      break;
  }

  const mat = mat3Translate(x, y);
  mat3Mult(mat3RotateAroundPoint(x, y, radians), mat, mat);
  const planOutline = polyLine2Transform(
    rect2ByTwoPoints(
      mirrorPropX ? -bb.minX : bb.minX,
      mirrorPropY ? -bb.minY : bb.minY,
      mirrorPropX ? -bb.maxX : bb.maxX,
      mirrorPropY ? -bb.maxY : bb.maxY,
    ),
    mat,
  );

  const { minX, minY, maxX, maxY } = polyLine2AABB2(planOutline);
  const alignedAABB3 = aabb3New(minX, minY, bb.minZ + z, maxX, maxY, bb.maxZ + z);

  const sx = scaleX;
  const sy = uniformScale ? scaleX : scaleY;
  const sz = uniformScale ? scaleX : scaleZ;

  return propNew({
    room,
    src: propId as any,
    x,
    y,
    z,
    rotationDegrees: radianToDegree(radians),
    scaleX: sx * (mirrorPropX ? -1 : 1),
    scaleY: sy * (mirrorPropY ? -1 : 1),
    scaleZ: sz,
    mirrorPropX,
    mirrorPropY,
    handle,
    handleAuxiliary,
    labels,
    floorMaterial,
    descriptor,
    catalogType,
    materialOverrides,
    useExteriorPalette,
    planOutline,
    alignedAABB3,
    useAlignmentCalc: !isDeprecatedWallProp,
    alignmentX,
    alignmentY,
    entityInstanceType,
    floorPlanTag,
    floorPlanTagOffsetX,
    floorPlanTagOffsetY,
    isExterior,
  });
}

function propLateralJustificationAdjustment(
  minY: number,
  maxY: number,
  lateralAlignment: PropInstanceLateralAlignment,
): number {
  const length = maxY - minY;
  const halfLength = length / 2;
  switch (lateralAlignment) {
    case PropInstanceLateralAlignment.OUTSIDE_FACE:
      return minY;
    case PropInstanceLateralAlignment.INSIDE_FACE:
      return length + minY;
    case PropInstanceLateralAlignment.CENTERLINE:
      return halfLength + minY;
    case PropInstanceLateralAlignment.ORIGIN:
      return 0;
    default:
      assertUnreachable(lateralAlignment);
  }
}

function propParamJustificationAdjustment(
  minX: number,
  maxX: number,
  horizontalAlignment: PropInstanceHorizontalAlignment,
): number {
  const width = maxX - minX;
  const halfWidth = width / 2;
  switch (horizontalAlignment) {
    case PropInstanceHorizontalAlignment.CENTER:
      return minX + halfWidth;
    case PropInstanceHorizontalAlignment.RIGHT:
      return minX + width;
    case PropInstanceHorizontalAlignment.LEFT:
      return minX;
    case PropInstanceHorizontalAlignment.ORIGIN:
      return 0;
    default:
      assertUnreachable(horizontalAlignment);
  }
}

function propVerticalJustificationAdjustment(
  minZ: number,
  maxZ: number,
  verticalAlignment: PropInstanceVerticalAlignment,
): number {
  const height = maxZ - minZ;
  const halfHeight = height / 2;
  switch (verticalAlignment) {
    case PropInstanceVerticalAlignment.TOP:
      return minZ + height;
    case PropInstanceVerticalAlignment.CENTER:
      return minZ + halfHeight;
    case PropInstanceVerticalAlignment.BOTTOM:
      return minZ;
    case PropInstanceVerticalAlignment.ORIGIN:
      return 0;
    default:
      assertUnreachable(verticalAlignment);
  }
}

export function mergedVerticalOffset(propOffset?: number, propAttributesOffset?: number) {
  if (propOffset !== undefined) {
    return propOffset;
  }
  if (propAttributesOffset !== undefined) {
    return propAttributesOffset;
  }
  return 0;
}
